import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { FuseConfigService } from "@fuse/services/config.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { FuseNavigation } from "@fuse/types";
import { TranslateService } from "@ngx-translate/core";
import { SubscriptionService } from "app/core/service/subscription.service";
import { GetDataDTO } from "app/data/schema/Common/GetDataDTO";
import { SessionDTO } from "app/data/schema/Identity/SessionDTO";
import { SubscriptionDTO } from "app/data/schema/Subscription/SubscriptionDTO";
import { ApiUsersService } from "app/data/service/api-users.service";
import { ApiSubscriptionsService } from "app/data/service/subscription/api-subscriptions.service";
import { NavigationService } from "app/navigation/navigation.service";
import * as _ from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, mergeMap, takeUntil } from "rxjs/operators";
import { AuthService } from "../../../core/service/auth.service";
import { SupportService } from "../../../core/service/support.service";
import { locale as english } from "./i18n/en";
import { locale as spanish } from "./i18n/es";
import { locale as portuguese } from "./i18n/pt";

@Component({
    selector: "toolbar",
    templateUrl: "./toolbar.component.html",
    styleUrls: ["./toolbar.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class ToolbarComponent implements OnInit, OnDestroy {
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: FuseNavigation[];
    selectedLanguage: any;
    userStatusOptions: any[];
    userName: string;

    search$: BehaviorSubject<string>;
    subscriptionsData: GetDataDTO<SubscriptionDTO>;
    currentSubscription: SubscriptionDTO;
    showSubscriptionSelector: boolean;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private authService: AuthService,
        private _navigationService: NavigationService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _apiSubscriptionsService: ApiSubscriptionsService,
        private _apiUserService: ApiUsersService,
        private _subscriptionService: SubscriptionService,
        public supportService: SupportService
    ) {
        this.authService.getLoggedUser();
        this.userName = authService.getClaimsSub();
        // Set the defaults
        this.userStatusOptions = [
            {
                title: "Online",
                icon: "icon-checkbox-marked-circle",
                color: "#4CAF50"
            },
            {
                title: "Away",
                icon: "icon-clock",
                color: "#FFC107"
            },
            {
                title: "Do not Disturb",
                icon: "icon-minus-circle",
                color: "#F44336"
            },
            {
                title: "Invisible",
                icon: "icon-checkbox-blank-circle-outline",
                color: "#BDBDBD"
            },
            {
                title: "Offline",
                icon: "icon-checkbox-blank-circle-outline",
                color: "#616161"
            }
        ];

        // Load the translations
        this._fuseTranslationLoaderService.loadTranslations(english, portuguese, spanish);

        this.languages = [
            {
                id: "en-US",
                title: "English",
                flag: "us"
            },
            {
                id: "pt-BR",
                title: "Português",
                flag: "br"
            },
            {
                id: "es-ES",
                title: "Español",
                flag: "es"
            }
        ];

        this._navigationService.getNavigationByLoggedUser(authService.getClaimsRoles()).subscribe((nav) => {
            this.navigation = nav;
        });

        this.showSubscriptionSelector = false;

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        this.search$ = new BehaviorSubject<string>("");
        this.subscriptionsData = new GetDataDTO<SubscriptionDTO>();
        this.currentSubscription = null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((settings) => {
            this.horizontalNavbar = settings.layout.navbar.position === "top";
            this.rightNavbar = settings.layout.navbar.position === "right";
            this.hiddenNavbar = settings.layout.navbar.hidden === true;
        });

        // Set the selected language from default languages
        this.selectedLanguage = _.find(this.languages, {
            id: this._translateService.currentLang
        });

        this._subscriptionService
            .currentSubscriptionAsync()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((s) => {
                this.currentSubscription = s;

                this.search$.next("");
            });

        this.search$
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(500),
                distinctUntilChanged(),
                mergeMap((filter) => {
                    if (this.authService.getClaimsRoles()?.includes("SuperAdmin"))
                        return this._apiSubscriptionsService.get(false, filter, "nome", "asc", 0, 10);
                    else
                        return this._apiUserService.getUserCrossSubscriptions(
                            this.authService.getClaimsUserId(),
                            filter,
                            0,
                            10
                        );
                })
            )
            .subscribe(
                (data: GetDataDTO<SubscriptionDTO>) => {
                    this.subscriptionsData = data;
                },
                () => {}
            );

        this.authService.loggedUserAsync.subscribe((user) => {
            if (!user) return;

            this.showSubscriptionSelector =
                this.authService.getClaimsRoles()?.includes("SuperAdmin") ||
                (this.authService.getClaimsRoles()?.includes("Admin") && user.crossSubscriptionUsers?.length > 0);
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value): void {
        // Do your search here...
        console.log(value);
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);
    }

    logout(): void {
        this.authService.logout().subscribe((_) => {
            window.location.href = "/";
        });
    }

    helpClick(): void {
        this.supportService.openWpp();
    }

    switchSubscription(targetSubscription: SubscriptionDTO): void {
        if (this._subscriptionService.currentSubscription.id == targetSubscription.id) return;

        const redirectPath = location.pathname.split(
            /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}.*/i
        )[0];

        this.authService.sessionAsync.pipe(takeUntil(this._unsubscribeAll)).subscribe((session: SessionDTO) => {
            const form = document.createElement("form");
            const element1 = document.createElement("input");
            const element2 = document.createElement("input");
            const element3 = document.createElement("input");

            form.method = "POST";
            form.action = `https://${targetSubscription.host}/api/identity/switchSubscription/login`;

            element1.value = targetSubscription.id;
            element1.name = "targetSubscriptionId";
            element1.type = "hidden";
            form.appendChild(element1);

            element2.value = redirectPath;
            element2.name = "redirectPath";
            element2.type = "hidden";
            form.appendChild(element2);

            element3.value = session.access_token;
            element3.name = "token";
            element3.type = "hidden";
            form.appendChild(element3);

            form.target = "_blank";

            document.body.appendChild(form);

            form.submit();
        });
    }
}
